// src/store/cartSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cartAPI } from '../api/cart.js';

export const getCart = createAsyncThunk(
  'cart/get',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartAPI.get();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/add',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await cartAPI.add(data);
      dispatch(getCart());
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await cartAPI.update(id, data);
      dispatch(getCart());
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update cart');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/remove',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await cartAPI.remove(id);
      dispatch(getCart());
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from cart');
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clear',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await cartAPI.clear();
      dispatch(getCart());
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear cart');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  count: 0,
  isLoading: false,
  // 🔑 `isLoaded` ОТЛИЧАЕТ «ЕЩЁ НЕ ЗАГРУЖАЛИ» ОТ «ЗАГРУЗИЛИ, ПУСТО».
  //    Без него оба состояния выглядят как `items = []`, и /checkout
  //    при ПРЯМОМ заходе (F5, ссылка) мгновенно уводил на /cart:
  //    на первом рендере items пуст, а isLoading уже false.
  //    Замер: GET /cart отдавал items: 1, но редирект случался раньше.
  isLoaded: false,
  error: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetCart: (state) => {
      state.items = [];
      state.total = 0;
      state.count = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;   // данные пришли — «пусто» теперь значит пусто
        state.items = action.payload.items || [];
        state.total = action.payload.total || 0;
        state.count = action.payload.count || 0;
      })
      .addCase(getCart.rejected, (state, action) => {
        state.isLoading = false;
        // Даже при ошибке снимаем неопределённость: иначе UI навсегда
        // застынет в «не знаю» и не покажет ни корзину, ни ошибку.
        state.isLoaded = true;
        state.error = action.payload;
      })
      .addCase(addToCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(removeFromCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeFromCart.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetCart } = cartSlice.actions;
export default cartSlice.reducer;